/**
 * The master registry.
 *
 * `routes.tsx` builds its route table from `MASTER_DEFINITIONS`, and each
 * sub-feature's API module reads its own path out of `MASTER_RESOURCES` -- so a
 * renamed endpoint or a relabelled menu item is a one-line change here rather
 * than a hunt through seventeen folders.
 */

/**
 * API resource segment per master, e.g. `categories` for `/categories`.
 *
 * `organization` and `entity` are confirmed against the backend
 * (`OrganizationController` maps `/api/v1/organizations`, `LegalEntityController`
 * maps `/api/v1/legal-entities`). Note that an organization is addressed by its
 * **code**, not by a numeric id, so its CRUD client cannot be the shared
 * factory unchanged -- see `organizationApi`.
 *
 * The masters built to the design reference agree their segment with the
 * backend capability of the same name. category deliberately does not use
 * categories, which belongs to configuration.master's lookup table.
 *
 * Two segments do not match their key, and both are deliberate. `material` is
 * served at `products`, because that is what the record is called everywhere
 * except in a folder name -- the design reference titles the screen "Product".
 * `pr` names `purchase-requests`, the live Purchase Requests feature's API: it
 * has no master screen of its own (`/masters/pr` redirects to Purchase Requests),
 * and the legacy Purchase Requisition master and its `/purchase-requisitions`
 * API were removed on 2026-10-16. The key stays for the sidebar and RBAC entry.
 */
export const MASTER_RESOURCES = {
  organization: 'organizations',
  /*
    The Category master is configuration.master's lookup catalogue, not the
    procurement categories at 'product-categories'. The two share a table and
    are different records: this screen manages User_Type, Approval_Status,
    Notification_Type and Yes_No, which a new entity is seeded eleven of; the
    other classifies materials and is read through `masterReferenceApi`.
  */
  category: 'categories',
  country: 'countries',
  state: 'states',
  city: 'cities',
  entity: 'legal-entities',
  'business-unit': 'business-units',
  division: 'divisions',
  department: 'departments',
  location: 'locations',
  uom: 'uoms',
  /* The record is a Product; only the folder says material. */
  material: 'products',
  currency: 'currencies',
  'payment-terms': 'payment-terms',
  'delivery-terms': 'delivery-terms',
  /* The eleven rules of Incoterms 2020, which a delivery term cites. */
  incoterm: 'incoterms',
  /* No master screen: `/masters/pr` redirects to the Purchase Requests feature, which owns this API. */
  pr: 'purchase-requests',
  supplier: 'suppliers',
  /* A reference list User Management's Designation field already reads; now a master screen too. */
  designation: 'designations',
  'user-management': 'users',
} as const;

export type MasterKey = keyof typeof MASTER_RESOURCES;

export interface MasterDefinition {
  /** Folder name, and the key into MASTER_RESOURCES. */
  key: MasterKey;
  /**
   * What the Masters menu shows -- and so what the RBAC matrix's Masters group shows too.
   *
   * Kept identical to the sidebar's own label (`MASTERS_MENU_CHILDREN` in `app/layout/constants.ts`,
   * which this feature may not import); `mastersMenu.test.tsx` asserts the two lists agree entry for
   * entry, in order.
   */
  label: string;
  /** Path segment under `/masters`. */
  path: string;
  /** PrimeIcons name for the page's icon tile. */
  icon: string;
  /**
   * The `os_feature.ofe_name` this master is governed by.
   *
   * Which feature a screen belongs to is a fact about the code, not about a role -- it is the same name
   * the backend guard names (`@perm.on('Payment Terms', 'READ')`). What a role may DO with it is
   * never written here: that comes from `GET /users/me`, per role, at run time.
   *
   * Stated per master rather than derived from `label`, because two do not match: `material` is labelled
   * "Product" against the feature **Product** only by coincidence of the rename, and `pr` (an entry that
   * opens the Purchase Requests feature, not a master screen) "Purchase Requests" against **Purchase
   * Requests**. Deriving it would silently hide such a master from a role that may use it.
   */
  feature: string;
}

/**
 * Every master, in the Masters menu's order.
 *
 * The order is load-bearing: `MastersLanding` lands on the first readable entry, and the RBAC matrix
 * draws its Masters group in this sequence (`features/rbac/models/masterOrder.ts`). It is the sidebar's
 * order -- Organization, then the hierarchy, then the commercial masters, ending at User Management.
 */
export const MASTER_DEFINITIONS: readonly MasterDefinition[] = [
  { key: 'organization', label: 'Organization', path: 'organization', icon: 'pi-building', feature: 'Organization', },
  { key: 'entity', label: 'Entity', path: 'entity', icon: 'pi-building', feature: 'Entity', },
  {
    key: 'business-unit',
    label: 'Business Unit',
    path: 'business-unit',
    icon: 'pi-sitemap', feature: 'Business Unit',
  },
  { key: 'division', label: 'Division', path: 'division', icon: 'pi-share-alt', feature: 'Division', },
  { key: 'department', label: 'Department', path: 'department', icon: 'pi-users', feature: 'Department', },
  { key: 'location', label: 'Location', path: 'location', icon: 'pi-map-marker', feature: 'Location', },
  { key: 'category', label: 'Category', path: 'category', icon: 'pi-tags', feature: 'Category', },
  { key: 'uom', label: 'Unit of Measurement', path: 'uom', icon: 'pi-calculator', feature: 'Unit of Measurement', },
  { key: 'country', label: 'Country', path: 'country', icon: 'pi-globe', feature: 'Country', },
  { key: 'state', label: 'State', path: 'state', icon: 'pi-map', feature: 'State', },
  { key: 'city', label: 'City', path: 'city', icon: 'pi-map-marker', feature: 'City', },
  { key: 'material', label: 'Product', path: 'material', icon: 'pi-box', feature: 'Product', },
  /* design16's icons (2026-10-21), drawn by assets/icons/customIcons.css. */
  { key: 'currency', label: 'Currency', path: 'currency', icon: 'pi-currency-coins', feature: 'Currency', },
  {
    key: 'payment-terms',
    label: 'Payment Terms',
    path: 'payment-terms',
    icon: 'pi-payment-term', feature: 'Payment Terms',
  },
  {
    key: 'delivery-terms',
    label: 'Delivery Terms',
    path: 'delivery-terms',
    icon: 'pi-truck', feature: 'Delivery Terms',
  },
  /*
    Beside Delivery Terms, which is the only master that cites it and the screen a user arrives here from.
    V20261011110100 seeds its feature row.
  */
  { key: 'incoterm', label: 'Incoterm', path: 'incoterm', icon: 'pi-send', feature: 'Incoterm', },
  /* One RBAC item with the Purchase Requests screen, which this entry opens (V20261005150000). */
  /* Named and drawn as design16 names it; the RBAC feature keeps its own name. */
  { key: 'pr', label: 'Purchase Request', path: 'pr', icon: 'pi-clipboard', feature: 'Purchase Requests', },
  { key: 'supplier', label: 'Supplier', path: 'supplier', icon: 'pi-briefcase', feature: 'Supplier', },
  /* V20260928120000 gave it the feature row; the endpoints have always existed. */
  { key: 'designation', label: 'Designation', path: 'designation', icon: 'pi-id-card', feature: 'Designation', },
  {
    key: 'user-management',
    label: 'User Management',
    path: 'user-management',
    icon: 'pi-user', feature: 'User Management',
  },
];

/**
 * The masters the UI does not offer, because the platform seeds them.
 *
 * Review sheet #12 (2026-10-10): Country, State and City are reference data. The platform ships India, its
 * states and its major cities as seed data, and nobody maintains them through a screen -- so the three leave
 * the Masters submenu for every role, and leave the RBAC screen with them.
 *
 * <h2>Why they are listed here and not simply removed from MASTER_DEFINITIONS</h2>
 *
 * Dropping the three entries would hide them in both places at once, and it would also, quietly, take the
 * guard off their routes: `featureOfMaster` would answer `undefined`, which `routes.tsx` reads as "no feature
 * to test" and `MasterScreen` reads as "do not narrow the definition". The screens stay reachable by URL, so
 * that is a loosening, not a tidy-up. The registry therefore keeps all nineteen masters -- route, guard,
 * feature and permissions exactly as they were -- and this list says only which of them the UI omits.
 *
 * <h2>What reads it</h2>
 *
 * - `MASTERS_MENU_CHILDREN` (`app/layout/constants.ts`) has the three entries commented out against this list;
 * - `isMenuMaster` (`features/rbac/models/masterOrder.ts`) drops their rows from the matrix's Masters group.
 *   A row that is not drawn is *retained*, not discarded, so no role loses a grant it holds;
 * - `MastersLanding` lands on the first master that is both visible and readable.
 *
 * Restoring a master is taking its key out of here and uncommenting its menu entry. Nothing else moves.
 */
export const UI_HIDDEN_MASTER_KEYS: readonly MasterKey[] = ['country', 'state', 'city'];

/** The masters the Masters menu lists, in its order -- `MASTER_DEFINITIONS` less the seeded three. */
export const VISIBLE_MASTER_DEFINITIONS: readonly MasterDefinition[] = MASTER_DEFINITIONS.filter(
  (definition) => !UI_HIDDEN_MASTER_KEYS.includes(definition.key),
);

/** The first master, and where a bare `/masters` redirects to. */
export const MASTER_DEFAULT_PATH = 'organization';

/** The design's default: no status filter applied. */
export const MASTER_ALL_STATUS = '';

/**
 * The two statuses a master record can be put into, as the endpoints name them.
 *
 * There is a third, `Deleted`, which a soft delete writes and no listing returns. It is deliberately absent
 * here: it is not a state anyone picks, and offering it in the filter would advertise records the master has
 * removed. The server excludes it from every listing whatever the filter says.
 */
export const MASTER_ACTIVE_STATUS = 'Active';
export const MASTER_INACTIVE_STATUS = 'Inactive';

/**
 * What the status control offers.
 *
 * The labels are the `statusName` values the master endpoints return, so the
 * value is sent through as-is rather than translated to a `statusId` -- the
 * id set is not documented per master.
 */
export const MASTER_STATUS_OPTIONS: readonly { label: string; value: string }[] = [
  { label: 'All Status', value: MASTER_ALL_STATUS },
  { label: 'Active', value: MASTER_ACTIVE_STATUS },
  { label: 'Inactive', value: MASTER_INACTIVE_STATUS },
];

export const MASTER_DEFAULT_PAGE_SIZE = 10;
export const MASTER_PAGE_SIZE_OPTIONS: readonly number[] = [10, 25, 50, 100];
/** The page-size list's width -- wide enough for the list, which the 104px control is not. */
export const MASTER_PAGE_SIZE_PANEL_WIDTH = 140;

/** Typing in the search box should not fire a request per keystroke. */
export const MASTER_SEARCH_DEBOUNCE_MS = 350;

export const MASTER_TOAST_LIFE_MS = 3000;

/** Shown when a call fails and the server described nothing useful. */
export const MASTER_FALLBACK_ERROR = 'Server error, please try again';

/**
 * The feature a master is governed by, read off its own definition.
 *
 * There is no list of features here. Each master says which one it is, beside its title and its icon,
 * and what a role may *do* with it is answered at run time by the grants `GET /users/me` returns.
 */
export function featureOfMaster(key: string): string | undefined {
  return MASTER_DEFINITIONS.find((definition) => definition.key === key)?.feature;
}
